import { useState } from 'react'
import { Camera, ChevronRight, ExternalLink, MessageSquare, Zap, Search, AlertTriangle, Bot, Terminal } from 'lucide-react'
import type { TestRunScreenshot, TestRunLog } from '../data/projectTypes'
import './ScreenshotTimeline.css'

interface ScreenshotTimelineProps {
  screenshots: TestRunScreenshot[]
  logs?: TestRunLog[]
}

type TimelineEvent =
  | { kind: 'screenshot'; data: TestRunScreenshot; time: string }
  | { kind: 'log'; data: TestRunLog; time: string }

function formatTime(iso: string) {
  const d = new Date(iso)
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

const LOG_TYPE_ICONS: Record<string, typeof MessageSquare> = {
  action: Zap,
  discovery: Search,
  error: AlertTriangle,
  agent: Bot,
  container: Terminal,
}

const LOG_TYPE_LABELS: Record<string, string> = {
  info: 'Info',
  action: 'Action',
  discovery: 'Discovery',
  error: 'Error',
  agent: 'Agent',
  container: 'Container',
}

export default function ScreenshotTimeline({ screenshots, logs = [] }: ScreenshotTimelineProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState(true)

  const events: TimelineEvent[] = [
    ...screenshots.map((s): TimelineEvent => ({ kind: 'screenshot', data: s, time: s.created_at })),
    ...logs.map((l): TimelineEvent => ({ kind: 'log', data: l, time: l.created_at })),
  ].sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())

  if (events.length === 0) return null

  const screenshotCount = screenshots.length
  const logCount = logs.length

  return (
    <section className="screenshot-timeline" data-testid="screenshot-timeline">
      <button
        className="screenshot-timeline__heading screenshot-timeline__heading--toggle"
        onClick={() => setCollapsed(c => !c)}
        aria-expanded={!collapsed}
        data-analytics-event="replay-timeline-toggle"
      >
        <ChevronRight size={16} className={`screenshot-timeline__chevron${collapsed ? '' : ' screenshot-timeline__chevron--open'}`} aria-hidden="true" />
        <Camera size={16} aria-hidden="true" />
        Run Activity ({screenshotCount} screenshot{screenshotCount !== 1 ? 's' : ''}{logCount > 0 ? `, ${logCount} log${logCount !== 1 ? 's' : ''}` : ''})
      </button>
      {!collapsed && <div className="screenshot-timeline__list">
        {events.map((event, i) => {
          if (event.kind === 'screenshot') {
            const ss = event.data
            return (
              <div key={ss.id} className="screenshot-timeline__item" data-testid={`screenshot-${i}`}>
                <div className="screenshot-timeline__marker">
                  <span className="screenshot-timeline__dot" />
                  {i < events.length - 1 && <span className="screenshot-timeline__line" />}
                </div>
                <div className="screenshot-timeline__content">
                  <div className="screenshot-timeline__meta">
                    <span className="screenshot-timeline__time">{formatTime(ss.created_at)}</span>
                    {ss.page_url && (
                      <span className="screenshot-timeline__url" title={ss.page_url}>
                        <ExternalLink size={12} aria-hidden="true" />
                        {ss.page_url}
                      </span>
                    )}
                  </div>
                  {ss.caption && <p className="screenshot-timeline__caption">{ss.caption}</p>}
                  {ss.screenshot_url && (
                    <button
                      className="screenshot-timeline__thumb-btn"
                      onClick={() => setExpandedId(expandedId === ss.id ? null : ss.id)}
                      aria-expanded={expandedId === ss.id}
                      data-analytics-event="replay-timeline-screenshot"
                    >
                      <img
                        src={ss.screenshot_url}
                        alt={ss.caption || `Screenshot ${i + 1}`}
                        className={`screenshot-timeline__img ${expandedId === ss.id ? 'screenshot-timeline__img--expanded' : ''}`}
                        loading="lazy"
                      />
                    </button>
                  )}
                </div>
              </div>
            )
          }

          const log = event.data
          const LogIcon = LOG_TYPE_ICONS[log.log_type] ?? MessageSquare
          const logLabel = LOG_TYPE_LABELS[log.log_type] ?? 'Log'

          return (
            <div key={log.id} className="screenshot-timeline__item screenshot-timeline__item--log" data-testid={`log-${i}`}>
              <div className="screenshot-timeline__marker">
                <span className={`screenshot-timeline__dot screenshot-timeline__dot--${log.log_type}`} />
                {i < events.length - 1 && <span className="screenshot-timeline__line" />}
              </div>
              <div className="screenshot-timeline__content screenshot-timeline__content--log">
                <div className="screenshot-timeline__meta">
                  <span className="screenshot-timeline__time">{formatTime(log.created_at)}</span>
                  <span className={`screenshot-timeline__log-badge screenshot-timeline__log-badge--${log.log_type}`}>
                    <LogIcon size={12} aria-hidden="true" />
                    {logLabel}
                  </span>
                </div>
                <p className="screenshot-timeline__log-message">{log.message}</p>
              </div>
            </div>
          )
        })}
      </div>}
    </section>
  )
}
